<template>
  <div class="interview-list">
    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="3" animated />
    </div>
    
    <div v-else-if="interviews.length === 0" class="empty-container">
      <el-empty description="暂无面试记录" />
      <el-button type="primary" @click="addInterview">添加面试</el-button>
    </div>
    
    <div v-else>
      <div class="interview-header">
        <h3 class="section-title">面试记录 ({{ interviews.length }})</h3>
        <el-button type="primary" size="small" @click="addInterview">
          <el-icon><Plus /></el-icon> 添加面试
        </el-button>
      </div>
      
      <el-timeline class="interview-timeline">
        <el-timeline-item
          v-for="interview in sortedInterviews"
          :key="interview.id"
          :timestamp="formatDateTime(interview.dateTime)"
          :type="getTimelineItemType(interview)"
          size="large"
          :hollow="interview.status === 'completed'"
        >
          <el-card class="interview-card">
            <template #header>
              <div class="card-header">
                <div class="stage-info">
                  <span class="stage-name">{{ getStageLabel(interview.stage) }}</span>
                  <el-tag size="small" :type="getStatusType(interview.status)">
                    {{ getStatusLabel(interview.status) }}
                  </el-tag>
                </div>
                <div class="card-actions">
                  <div v-if="interview.status !== 'completed'" class="icon-item" @click="completeInterview(interview)">
                    <el-icon class="custom-icon complete-icon"><Check /></el-icon>
                    <span class="icon-label">完成</span>
                  </div>
                  <div class="icon-item" @click="editInterview(interview)">
                    <el-icon class="custom-icon edit-icon"><Edit /></el-icon>
                    <span class="icon-label">编辑</span>
                  </div>
                  <div class="icon-item" @click="confirmDelete(interview)">
                    <el-icon class="custom-icon delete-icon"><Delete /></el-icon>
                    <span class="icon-label">删除</span>
                  </div>
                </div>
              </div>
            </template>
            
            <div class="interview-details">
              <div v-if="interview.interviewer" class="detail-item">
                <el-icon><User /></el-icon>
                <span>面试官: {{ interview.interviewer }}</span>
              </div>
              
              <div v-if="interview.location" class="detail-item">
                <el-icon><Location /></el-icon>
                <span>地点: {{ interview.location }}</span>
              </div>
              
              <div v-if="interview.duration" class="detail-item">
                <el-icon><Timer /></el-icon>
                <span>时长: {{ interview.duration }} 分钟</span>
              </div>
              
              <div v-if="interview.notes" class="detail-item notes">
                <el-icon><Memo /></el-icon>
                <span>备注: {{ interview.notes }}</span>
              </div>
              
              <div v-if="interview.preparationMaterials" class="detail-item">
                <el-icon><Document /></el-icon>
                <span>准备材料: {{ interview.preparationMaterials }}</span>
              </div>
              
              <div v-if="interview.feedback" class="detail-item feedback">
                <el-icon><ChatDotRound /></el-icon>
                <span>反馈: {{ interview.feedback }}</span>
              </div>
            </div>
          </el-card>
        </el-timeline-item>
      </el-timeline>
    </div>
    
    <!-- 面试表单对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="isEditing ? '编辑面试' : '添加面试'"
      width="650px"
      destroy-on-close
    >
      <interview-form
        :position-id="positionId"
        :interview="currentInterview"
        :editing="isEditing"
        @created="handleCreated"
        @updated="handleUpdated"
        @cancel="dialogVisible = false"
      />
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Plus, Edit, Delete, User, Location, Timer, Memo, Document, ChatDotRound, Check } from '@element-plus/icons-vue';
import type { Interview, UpdateInterview } from '@/types/interview';
import type { ApiResponse, PageResponse } from '@/types/common';
import { INTERVIEW_STAGES, INTERVIEW_STATUS } from '@/types/interview';
import interviewService from '@/services/interviewService';
import InterviewForm from './InterviewForm.vue';
import dayjs from 'dayjs';

const props = defineProps<{
  positionId: number;
}>();

const emit = defineEmits<{
  (e: 'refresh'): void;
}>();

const loading = ref(false);
const interviews = ref<Interview[]>([]);
const dialogVisible = ref(false);
const isEditing = ref(false);
const currentInterview = ref<UpdateInterview | undefined>(undefined);

// 按面试时间排序的面试列表
const sortedInterviews = computed(() => {
  return [...interviews.value].sort((a, b) => {
    return dayjs(a.dateTime).isAfter(dayjs(b.dateTime)) ? 1 : -1;
  });
});

// 页面加载时获取面试列表
onMounted(async () => {
  console.log('InterviewList组件加载，职位ID:', props.positionId);
  await fetchInterviews();
});

// 获取面试列表
const fetchInterviews = async () => {
  loading.value = true;
  try {
    const response = await interviewService.getInterviewsByPosition(props.positionId);
    console.log('API Response:', response); // 调试日志
    
    if (response && response.content) {
      interviews.value = response.content;
      console.log('Interviews loaded:', interviews.value); // 调试日志
    } else {
      console.warn('Invalid response format:', response); // 调试日志
      interviews.value = [];
    }
  } catch (error) {
    console.error('Failed to fetch interviews:', error);
    ElMessage.error('获取面试列表失败');
    interviews.value = [];
  } finally {
    loading.value = false;
  }
};

// 添加面试
const addInterview = () => {
  isEditing.value = false;
  currentInterview.value = undefined;
  dialogVisible.value = true;
};

// 编辑面试
const editInterview = (interview: Interview) => {
  // 创建编辑对象，只包含更新所需字段
  const editingInterview: UpdateInterview = {
    id: interview.id!,
    stage: interview.stage,
    dateTime: interview.dateTime,
    duration: interview.duration,
    location: interview.location,
    notes: interview.notes,
    feedback: interview.feedback,
    preparationMaterials: interview.preparationMaterials,
    status: interview.status,
    interviewer: interview.interviewer
  };
  
  isEditing.value = true;
  currentInterview.value = editingInterview;
  dialogVisible.value = true;
};

// 确认删除
const confirmDelete = (interview: Interview) => {
  ElMessageBox.confirm(
    `确定要删除此面试记录吗？${interview.stage ? `(${getStageLabel(interview.stage)})` : ''}`,
    '删除确认',
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }
  )
    .then(async () => {
      if (interview.id) {
        await deleteInterview(interview.id);
      }
    })
    .catch(() => {
      // 用户取消删除
    });
};

// 删除面试
const deleteInterview = async (id: number) => {
  try {
    const response = await interviewService.deleteInterview(id);
    ElMessage.success('面试删除成功');
    await fetchInterviews();
    emit('refresh');
  } catch (error) {
    console.error('删除面试失败:', error);
    ElMessage.error('删除面试失败');
  }
};

// 处理创建面试成功
const handleCreated = async (interview: Interview) => {
  // 刷新面试列表以获取最新数据
  await fetchInterviews();
  dialogVisible.value = false;
  emit('refresh');
};

// 处理更新面试成功
const handleUpdated = async (interview: Interview) => {
  // 刷新面试列表以获取最新数据
  await fetchInterviews();
  dialogVisible.value = false;
  emit('refresh');
};

// 格式化日期时间
const formatDateTime = (dateTime: string) => {
  if (!dateTime) return '';
  return dayjs(dateTime).format('YYYY-MM-DD HH:mm');
};

// 获取面试阶段标签
const getStageLabel = (stage: string) => {
  return INTERVIEW_STAGES[stage as keyof typeof INTERVIEW_STAGES] || stage;
};

// 获取面试状态标签
const getStatusLabel = (status: string | null | undefined) => {
  if (!status) return '未设置';
  return INTERVIEW_STATUS[status as keyof typeof INTERVIEW_STATUS] || status;
};

// 获取状态对应的类型
const getStatusType = (status: string | null | undefined) => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'cancelled':
      return 'danger';
    case 'pending':
      return 'warning';
    default:
      return 'info';
  }
};

// 获取时间轴项目类型
const getTimelineItemType = (interview: Interview) => {
  switch (interview.status) {
    case 'completed':
      return 'success';
    case 'cancelled':
      return 'danger';
    case 'pending':
      return interview.dateTime && dayjs(interview.dateTime).isBefore(dayjs()) ? 'warning' : 'primary';
    default:
      return 'info';
  }
};

// 修改完成面试的方法
const completeInterview = async (interview: Interview) => {
  try {
    const response = await interviewService.updateInterview({
      id: interview.id!,
      status: 'completed',
      stage: interview.stage,
      dateTime: interview.dateTime
    });
    
    ElMessage.success('面试已标记为完成');
    await fetchInterviews();
    emit('refresh');
  } catch (error) {
    console.error('更新面试状态失败:', error);
    ElMessage.error('操作失败，请重试');
  }
};
</script>

<style scoped>
.interview-list {
  margin-top: 20px;
}

.loading-container, .empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px;
  gap: 20px;
}

.interview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.interview-timeline {
  margin-top: 20px;
}

.interview-card {
  border-radius: 8px;
  overflow: hidden;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stage-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.stage-name {
  font-weight: 600;
  font-size: 16px;
}

.card-actions {
  display: flex;
  gap: 24px;
  padding: 8px 0;
}

.icon-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.icon-item:hover {
  background-color: var(--el-fill-color-light);
}

.custom-icon {
  font-size: 16px;
  transition: transform 0.3s ease;
}

.icon-label {
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.complete-icon {
  color: var(--el-color-success);
}

.edit-icon {
  color: var(--el-color-primary);
}

.delete-icon {
  color: var(--el-color-danger);
}

.icon-item:hover .custom-icon {
  transform: scale(1.1);
}

.interview-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: #606266;
}

.detail-item .el-icon {
  margin-top: 3px;
  flex-shrink: 0;
}

.detail-item.notes, .detail-item.feedback {
  padding: 8px;
  background-color: #f8f9fa;
  border-radius: 4px;
  margin-top: 4px;
}

.detail-item.feedback {
  color: #409eff;
  background-color: #ecf5ff;
}
</style> 